import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';
import { validateEmail, validateRequiredString } from '@utriper/core';
import { Box, Button, Grid2, InputAdornment, Link, Tooltip, Typography } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { Autocomplete, CheckboxComponent, LoadingIndicator, Textarea, TextInput } from '@utriper/core-ui';
import { nationalityList } from 'core/constants';
import DatePicker from 'components/Input/DatePicker';
import TermsAndConditions from 'components/TermsAndConditions';

const TermsAndConditionsLabel = ({ onClick = () => {}, translate = () => {} }) => {
    return (
        <>
            <Typography>{translate('terms_and_conditions.start')}</Typography>
            <Link component="button" type="button" onClick={onClick}>
                {translate('terms_and_conditions.end')}
            </Link>
        </>
    );
};

const CheckinForm = ({ initialValues = {}, onSubmit = () => {}, loading = false }) => {
    const { t } = useTranslation();
    const translate = (key, options) => t(`checkin.checkin_form.${key}`, options);
    const [openTerms, setOpenTerms] = useState(false);
    const nationalityOptions = nationalityList.map((nationality) => ({
        id: nationality?.en_short_name,
        label: nationality?.en_short_name,
    }));
    const handleSubmit = (values) => {
        return onSubmit(values);
    };

    const validatePassport = (value) => {
        if (!value) return false;
        if (!/^[a-zA-Z0-9]+$/.test(value)) {
            return false;
        }
        return true;
    };

    const validateForm = (values) => {
        const errors = {};

        if (!values?.termsAccepted) {
            errors.termsAccepted = translate('validations.terms_accepted_required');
        }

        const tripers = values?.tripers || [];
        errors.tripers = tripers.map((triper) => {
            const error = {};

            if (!validateRequiredString(triper?.firstName)) {
                error.firstName = translate('validations.first_name_required');
            }

            if (!validateRequiredString(triper?.lastName)) {
                error.lastName = translate('validations.last_name_required');
            }

            if (!validateRequiredString(triper?.email)) {
                error.email = translate('validations.email_required');
            }

            if (!validateEmail(triper?.email)) {
                error.email = translate('validations.invalid_email');
            }

            if (!triper?.dob || Number.isNaN(triper?.dob?.getTime())) {
                error.dob = translate('validations.dob_required');
            }

            if (!error?.dob && triper?.dob && triper?.dob?.getTime() > Date.now()) {
                error.dob = translate('validations.invalid_dob');
            }

            if (triper?.personalIdExpireDay && Number.isNaN(triper?.personalIdExpireDay?.getTime())) {
                error.personalIdExpireDay = translate('validations.invalid_personal_id_expire_day');
            }

            if (!error?.personalIdExpireDay && triper?.personalIdExpireDay && triper?.personalIdExpireDay?.getTime() < Date.now()) {
                error.personalIdExpireDay = translate('validations.invalid_personal_id_expire_day_future');
            }

            if (!triper?.nationality) {
                error.nationality = translate('validations.nationality_required');
            }

            if (!validateRequiredString(triper?.personalId)) {
                error.personalId = translate('validations.personal_id_required');
            }

            if (!validatePassport(triper?.personalId)) {
                error.personalId = translate('validations.invalid_personal_id');
            }

            return error;
        });

        return errors;
    };

    return (
        <Form
            onSubmit={handleSubmit}
            validate={validateForm}
            initialValues={{ ...initialValues }}
            mutators={{ ...arrayMutators }}
            render={({ handleSubmit, form, values }) => (
                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        mx: 'auto',
                        px: 3,
                        boxSizing: 'border-box',
                    }}>
                    <Typography variant="h4" component="h2" fontSize={20} fontWeight={900} sx={{ color: '#131313' }}>
                        {translate('title')}
                    </Typography>

                    <FieldArray id="tripers" name="tripers">
                        {({ fields }) => (
                            <Grid2 container size={12} mt={2} direction="column" spacing={2}>
                                {fields.map((name, index) => {
                                    const triper = values?.tripers[index];

                                    if (!triper) return '';

                                    return (
                                        <Grid2 container size={12}>
                                            <Typography variant="h6" fontSize={18} sx={{ color: '#131313' }}>
                                                {translate('triper_count', { count: index + 1 })}
                                            </Typography>
                                            {/* FULL NAME */}
                                            <Grid2 container size={12} spacing={2}>
                                                <Grid2 size={{ xs: 12, md: 6 }}>
                                                    <Field name={`${name}.firstName`} component={TextInput} label={translate('first_name')} />
                                                </Grid2>
                                                <Grid2 size={{ xs: 12, md: 6 }}>
                                                    <Field name={`${name}.lastName`} component={TextInput} label={translate('last_name')} />
                                                </Grid2>
                                            </Grid2>

                                            {/* EMAIL AND BIRTH DATE */}
                                            <Grid2 container size={12} spacing={2}>
                                                <Grid2 size={{ xs: 12, md: 6 }}>
                                                    <Field name={`${name}.email`} component={TextInput} label={translate('email')} />
                                                </Grid2>
                                                <Grid2 size={{ xs: 12, md: 6 }}>
                                                    <Field name={`${name}.dob`} component={DatePicker} label={translate('dob')} disableFuture />
                                                </Grid2>
                                            </Grid2>

                                            {/* PASSPORT DATA */}
                                            <Grid2 container size={12} spacing={2}>
                                                <Grid2 size={{ xs: 12, md: 6 }}>
                                                    <Field
                                                        name={`${name}.personalId`}
                                                        component={TextInput}
                                                        label={translate('personal_id')}
                                                        slotProps={{
                                                            input: {
                                                                endAdornment: (
                                                                    <InputAdornment position="end">
                                                                        <Tooltip title={translate('personal_id_tooltip')}>
                                                                            <InfoOutlined fontSize="small" color="action" />
                                                                        </Tooltip>
                                                                    </InputAdornment>
                                                                ),
                                                            },
                                                        }}
                                                    />
                                                </Grid2>
                                                <Grid2 size={{ xs: 12, md: 6 }}>
                                                    <Field name={`${name}.personalIdExpireDay`} component={DatePicker} label={translate('personal_id_expire_day')} disablePast />
                                                </Grid2>
                                            </Grid2>

                                            {/* NATIONALITY AND ADDITIONAL */}
                                            <Grid2 container size={12} spacing={2}>
                                                <Grid2 size={{ xs: 12, md: 6 }}>
                                                    <Field name={`${name}.nationality`} component={Autocomplete} label={translate('nationality')} options={nationalityOptions} valueAsId />
                                                </Grid2>
                                                <Grid2 size={{ xs: 12, md: 6 }}>
                                                    <Field name={`${name}.additional`} component={Textarea} label={translate('additional_label')} placeholder={translate('additional_placeholder')} minRows={4} />
                                                </Grid2>
                                            </Grid2>
                                        </Grid2>
                                    );
                                })}
                            </Grid2>
                        )}
                    </FieldArray>

                    <Grid2 container size={12} direction="column" alignItems="flex-end" spacing={2}>
                        <Field
                            name="termsAccepted"
                            type="checkbox"
                            // TODO: Remove this and use a decent checkbox component.
                            render={({ input, meta }) => (
                                <CheckboxComponent
                                    labelComponent={
                                        <TermsAndConditionsLabel
                                            onClick={(e) => {
                                                e?.preventDefault();
                                                setOpenTerms(true);
                                            }}
                                            translate={translate}
                                        />
                                    }
                                    checked={values?.termsAccepted}
                                    onFieldChange={(checked) => {
                                        form.change('termsAccepted', checked);
                                    }}
                                    {...input}
                                    {...meta}
                                />
                            )}
                        />

                        <Button
                            sx={{
                                backgroundColor: '#137DC5',
                                color: 'white',
                                textTransform: 'none',
                                fontWeight: 700,
                            }}
                            type="submit"
                            variant="contained"
                            onClick={(e) => handleSubmit(e)}
                            disabled={loading}
                            loading={loading}
                            loadingIndicator={<LoadingIndicator />}>
                            {translate('submit_button')}
                        </Button>
                    </Grid2>

                    <TermsAndConditions
                        open={openTerms}
                        onClose={() => setOpenTerms(false)}
                        onClick={() => {
                            setOpenTerms(false);
                            form.change('termsAccepted', !values?.termsAccepted);
                        }}
                    />
                </Box>
            )}
        />
    );
};

export default CheckinForm;
